import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import './doughnut.css';

const DoughnutChart = ({ data, chartType, centerText, centerData, rating, totalRating }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const ctx = chartRef.current.getContext('2d');
    if (chartType === 'rating') {
      const centerTextPlugin = {
        id: 'centerTextPlugin',
        afterDraw: (chart) => {
          if (chart.config.type === 'doughnut' && chart.config.options.plugins.centerTextPlugin.text) {
            const { width, height, ctx } = chart;
            ctx.restore();
            ctx.font = 'bold 16px Poppins';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = '#000';

            let text = chart.config.options.plugins.centerTextPlugin.text;

            ctx.fillText(text, width / 2, height / 2);
            ctx.save();
          }
        },
      };

      Chart.register(centerTextPlugin);

      const chart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          datasets: [
            {
              data: [rating, totalRating - rating],
              backgroundColor: ['#00e676', '#e0e0e0'],
              borderWidth: 0,
            },
          ],
        },
        options: {
          cutout: '75%',
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
            },
            centerTextPlugin: {
              text: `${rating}/${totalRating}`,
            },
          },
        },
      });

      return () => {
        Chart.unregister(centerTextPlugin);
        chart.destroy();
      };
    } else {
      const centerTextPlugin = {
        id: 'centerTextPlugin',
        afterDraw: (chart) => {
          if (chart.config.type === 'doughnut' && chart.config.options.plugins.centerTextPlugin.text) {
            const { width, height, ctx } = chart;
            ctx.restore();
            ctx.font = 'bold 15px Poppins';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = '#000';

            let data = chart.config.options.plugins.centerTextPlugin.data;
            let text = chart.config.options.plugins.centerTextPlugin.text;

            ctx.fillText(data, width / 2, height / 2 - 10);
            ctx.font = '10px Poppins';
            ctx.fillText(text, width / 2, height / 2 + 10);

            ctx.save();
          }
        },
      };
      const drawInnerArc = {
        id: 'drawInnerArc',
        afterDatasetsDraw(chart) {
          if (chart.config.type === 'doughnut' && chart.config.options.plugins.drawInnerArc) {
            const {
              ctx,
              chartArea: { width, height },
              data,
            } = chart;

            const dataset = data.datasets[0];
            const total = dataset.data.reduce((acc, value) => acc + value, 0);
            if (total === 0) return;

            const maxIndex = dataset.data.indexOf(Math.max(...dataset.data));
            const maxColor = dataset.backgroundColor[maxIndex];

            let startAngle = -0.5 * Math.PI;
            let endAngle = startAngle;

            dataset.data.forEach((value, index) => {
              const segmentAngle = (value / total) * 2 * Math.PI;
              if (index === maxIndex) {
                endAngle = startAngle + segmentAngle;
                const innerRadius = 35;
                const outerRadius = 36;

                ctx.save();
                ctx.beginPath();
                ctx.arc(width / 2, height / 2, (innerRadius + outerRadius) / 2, startAngle, endAngle);
                ctx.strokeStyle = maxColor;
                ctx.lineWidth = outerRadius - innerRadius + 1;
                ctx.stroke();
                ctx.restore();
              }
              startAngle += segmentAngle;
            });
          }
        },
      };

      Chart.register(centerTextPlugin, drawInnerArc);

      const hasData = data.datasets && data.datasets[0].data.some((value) => value > 0);

      const chart = new Chart(ctx, {
        type: "doughnut",
        data: hasData
          ? {
            ...data,
            datasets: data.datasets.map((dataset) => ({
              ...dataset,
              data: dataset.data ? dataset.data.slice().reverse() : [],
              backgroundColor: dataset.backgroundColor ? dataset.backgroundColor.slice().reverse() : [],
              hoverBackgroundColor: dataset.hoverBackgroundColor
                ? dataset.hoverBackgroundColor.slice().reverse()
                : [],
            })),
            labels: data.labels ? data.labels.slice().reverse() : [],
          }
          : {
            labels: ["No Data"],
            datasets: [
              {
                data: [100],
                backgroundColor: ["#d3d3d3"],
                hoverBackgroundColor: ["#d3d3d3"],
              },
            ],
          },

        options: {
          cutout: '75%',
          elements: {
            arc: {
              borderWidth: 0,
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
              external: function (context) {
                let tooltipEl = document.getElementById('chartjs-tooltip');
                if (!tooltipEl) {
                  tooltipEl = document.createElement('div');
                  tooltipEl.id = 'chartjs-tooltip';
                  tooltipEl.innerHTML = '<div></div>';
                  document.body.appendChild(tooltipEl);
                }
                const tooltipModel = context.tooltip;
                if (tooltipModel.opacity === 0) {
                  tooltipEl.style.opacity = 0;
                  return;
                }

                if (tooltipModel.body) {
                  const bodyLines = tooltipModel.body.map((bodyItem) => bodyItem.lines);

                  let innerHtml = '<div style="display: flex; align-items: center;">';

                  if (chartType === 'designation') {
                    const color = tooltipModel.labelColors[0].backgroundColor;
                    const value = '<strong>' + tooltipModel.dataPoints[0].formattedValue + '</strong>%';
                    innerHtml += `<span class="chartjs-tooltip-key" style="background:${color};"></span>`;
                    innerHtml += `<span>${value}</span>`;
                    innerHtml += '</div>';
                    innerHtml += '<div>';
                    innerHtml += `<span>${tooltipModel.dataPoints[0].label}</span>`;
                  } else if (chartType === 'countdesignation') {
                    const color = tooltipModel.labelColors[0].backgroundColor;
                    const value = '<strong>' + tooltipModel.dataPoints[0].formattedValue + '</strong>%';
                    innerHtml += `<span class="chartjs-tooltip-key" style="background:${color};"></span>`;
                    innerHtml += `<span>${value}</span>`;
                    innerHtml += '</div>';
                    innerHtml += '<div>';
                    innerHtml += `<span>${tooltipModel.dataPoints[0].label}</span>`;
                  }
                  else {
                    bodyLines.forEach((body, i) => {
                      const color = tooltipModel.labelColors[i].backgroundColor;
                      innerHtml += `<span class="chartjs-tooltip-key" style="background:${color};"></span>`;

                      let value = '<strong>' + body[0] + '</strong>';
                      if (chartType === 'gender') {
                        value += '%';
                      }

                      innerHtml += `<span>${tooltipModel.dataPoints[i].label}: ${value}</span>`;
                    });
                  }

                  innerHtml += '</div>';

                  const tooltipRoot = tooltipEl.querySelector('div');
                  tooltipRoot.innerHTML = innerHtml;
                }

                const position = context.chart.canvas.getBoundingClientRect();

                tooltipEl.style.opacity = 1;
                tooltipEl.style.position = 'absolute';
                tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
                tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
                tooltipEl.style.fontFamily = tooltipModel.options.bodyFont.family;
                tooltipEl.style.fontSize = '12px';
                tooltipEl.style.padding = tooltipModel.options.padding + 'px ' + tooltipModel.options.padding + 'px';
                tooltipEl.style.pointerEvents = 'none';
                tooltipEl.style.backgroundColor = '#000522';
                tooltipEl.style.color = '#ffffff';
                tooltipEl.style.borderRadius = '4px';
                tooltipEl.style.width = 'auto';
                tooltipEl.style.maxWidth = '230px';
              },
            },
            centerTextPlugin: hasData ? { text: centerText, data: centerData } : null,
            drawInnerArc: hasData ? {} : null,
          },
        },
      });
      return () => {
        Chart.unregister(centerTextPlugin, drawInnerArc);
        chart.destroy();
      };
    }
  }, [data, centerText, centerData, chartType, rating, totalRating]);
  const formatLabel = (label, value) => {
    if (chartType === 'subscription') {
      return `${value} ${label}`;
    } else if (chartType === 'designation') {
      return `${label}`;
    } else if (chartType === 'gender') {
      return `${value}% ${label.charAt(0).toUpperCase() + label.slice(1).toLowerCase()}`;
    } else if (chartType === 'countdesignation') {
      return ` ${label}`;
    } else if (chartType === 'countgender') {
      return `${value}% ${label}`;
    }
    return `${label}: ${value}%`;
  };

  const hasData = data.datasets && data.datasets[0].data.some((value) => value > 0);

  return (
    <div className="doughnut-chart-wrapper">
      <div className={`doughnut-chart-container ${chartType === 'rating' ? 'rating-chart' : ''} ${!hasData ? 'no-data' : ''}`}>
        <canvas ref={chartRef} />
        {hasData && chartType !== 'rating' && (
          <div className="chart-legend">
            {data.labels.map((label, index) => (
              <div key={index} className="chart-legend-item">
                <div
                  className="chart-legend-circle"
                  style={{ backgroundColor: data.datasets[0].backgroundColor[index] }}
                ></div>
                <div className="chart-legend-label">{formatLabel(label, data.datasets[0].data[index])}</div>
              </div>
            ))}
          </div>
        )}
      </div></div>
  );
};

export default DoughnutChart;
